/* ONE PAGE, DESCRIBED FOR THE PREVIEW — step 6 of docs/sites-pictures-spec.md.
 *
 * Built the way Presentations is (deckLayout.js): the page is described ONCE,
 * here, with no React in it, and the preview draws what this says. The spec
 * (§2b) has the JSON brief and the GHL prompt read this same description in
 * step 7, so what she sees is what the builder is asked for. Until then they
 * read the sections themselves — the same `sectionValues`, so an empty section
 * is left out of all three alike.
 *
 * WHAT THE PREVIEW MAY NOT DO, and so what this never produces:
 *   - invent a word. Every string here is one she wrote, untouched. A section
 *     with nothing in it is left out, the rule the brief learned the hard way
 *     ("Marcus Reyes, CEO, Northwind Logistics").
 *   - invent a picture. Where the style puts one and she has none, the slot is
 *     a PLACEHOLDER that says what belongs there (her call, 2026-10-16:
 *     "placeholders are better if we want an image").
 *   - wear Brill's colours. No brand → a plain neutral look, and a note that
 *     says so, rather than her page in Brill's teal.
 *
 * ALL FOUR STYLES ARE DRAWN (Light & Feminine on 2026-10-23, then Bold,
 * Classic and Artistic the same day as a first draft to polish — her call:
 * "something to start from … get it generally working then we can polish").
 * A page with no style is drawn as Light & Feminine, and the note says so.
 * The preview can SHOW another style than the page's (`show`), to compare
 * on the same words; that never changes the page, and the note says which
 * one the page is actually set to.
 */

import { kindDef, pictureOf, rowsOf, sectionValues } from "./websiteSectionKinds";
import { styleDef } from "./siteStyles";

const str = (v) => String(v ?? "").trim();
const hex = (v) => (/^#?[0-9a-f]{6}$/i.test(str(v)) ? `#${str(v).replace(/^#/, "").toUpperCase()}` : "");
const isLink = (v) => /^https?:\/\/\S+$/i.test(str(v));

/** The styles the preview can draw today. */
export const DRAWN_STYLES = ["light_feminine", "bold", "classic", "artistic"];

/* The neutral look for a page with no brand: warm paper and ink, nothing of
   Brill's. Kept soft so it still reads as Light & Feminine. */
const NEUTRAL = {
  bg: "#FAF6F2", soft: "#F1E8E1", heading: "#3B3330", text: "#4A423E", muted: "#7D726C",
  button: "#3B3330", buttonText: "#FFFFFF", accent: "#D8C3B6", dark: "#3B3330", textOnDark: "#F6EFEA",
};
/* Stand-in fonts when she has none, chosen per style — Bold in a serif would
   not read as Bold. Google fonts, loaded by name. */
const NEUTRAL_FONTS = {
  light_feminine: { heading: "Cormorant Garamond", body: "Lato" },
  bold: { heading: "Montserrat", body: "Inter" },
  classic: { heading: "Libre Baskerville", body: "Source Sans 3" },
  artistic: { heading: "DM Serif Display", body: "Work Sans" },
};

/* Mix two #RRGGBB colours — the soft panel behind a card is her background
   pulled a little toward her accent, so it is always one of her colours. */
function mix(a, b, t) {
  const p = (h) => [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16));
  const [x, y] = [p(a), p(b)];
  return `#${x.map((v, i) => Math.round(v + (y[i] - v) * t).toString(16).padStart(2, "0")).join("").toUpperCase()}`;
}

/** Her colours and fonts, by the job each does on the page, with what fell
 *  back to neutral named in `notes`. */
export function pageLook(skin = {}, styleId = "light_feminine") {
  const stand = NEUTRAL_FONTS[styleId] || NEUTRAL_FONTS.light_feminine;
  const notes = [];
  const pick = (k) => hex(skin[k]);
  const any = ["bgPrimary", "brandPrimary", "brandAccent", "accent", "textOnLight"].some(pick);
  if (!any) notes.push("No brand colours yet, so this is drawn in a plain neutral look. Set yours in Me → Brand.");
  const bg = pick("bgPrimary") || NEUTRAL.bg;
  const accent = pick("accent") || pick("highlight") || NEUTRAL.accent;
  const colours = {
    bg,
    soft: mix(bg, accent, 0.12),
    /* A block of her colour (Artistic): her accent, eased toward the page so
       dark words still read on it. A third of her accent, not half: at half
       her coral filled the About band, the cards and the closing band and
       read as a warning rather than a texture (her read, 2026-10-23). */
    block: mix(accent, bg, 0.68),
    heading: pick("brandPrimary") || NEUTRAL.heading,
    text: pick("textOnLight") || NEUTRAL.text,
    muted: mix(pick("textOnLight") || NEUTRAL.text, bg, 0.35),
    button: pick("brandAccent") || pick("brandPrimary") || NEUTRAL.button,
    buttonText: pick("buttonText") || NEUTRAL.buttonText,
    accent,
    highlight: pick("highlight") || accent,
    dark: pick("bgDark") || pick("brandPrimary") || NEUTRAL.dark,
    textOnDark: pick("textOnDark") || NEUTRAL.textOnDark,
  };
  const fonts = { heading: str(skin.fontHeading), body: str(skin.fontBody) };
  if (!fonts.heading || !fonts.body) {
    notes.push(`No ${!fonts.heading && !fonts.body ? "fonts" : !fonts.heading ? "heading font" : "body font"} set, so ${[!fonts.heading && stand.heading, !fonts.body && stand.body].filter(Boolean).join(" and ")} stand${!fonts.heading && !fonts.body ? "" : "s"} in.`);
  }
  return { colours, fonts: { heading: fonts.heading || stand.heading, body: fonts.body || stand.body }, notes };
}

/* THE ONE LINE IN ITALIC — the style's signature ("a serif headline with one
   line set in italic"). Only where HER headline already has a break: a new
   line, a dash, a colon or a comma. The italic is the part after it. Without
   one, nothing is italic — the preview never decides which of her words to
   lean on. */
export function splitHeadline(text) {
  const t = str(text);
  if (!t) return { lead: "", italic: "" };
  const nl = t.split(/\s*\n+\s*/);
  if (nl.length > 1) return { lead: nl[0], italic: nl.slice(1).join(" ") };
  const m = t.match(/^(.{6,}?)(\s+[—–-]\s+|:\s+|,\s+)(.{3,})$/);
  if (!m) return { lead: t, italic: "" };
  /* A comma or a colon stays with the first line; a dash is the break itself. */
  const sep = m[2].trim();
  return { lead: m[1] + (sep === "," || sep === ":" ? sep : ""), italic: m[3] };
}

/* A picture slot: her link, or a placeholder saying what belongs there. */
const slot = (url, label) => (isLink(url) ? { url: str(url), label } : { url: "", label });

/* Each kind, as the preview draws it. Everything is copied straight out of
   `content`; a list row with nothing in it is dropped. */
function describe(section, identity) {
  const kind = kindDef(section.sectionType);
  const c = section.content || {};
  const listOf = (key) => {
    const f = kind.fields.find((x) => x.key === key);
    return f ? rowsOf(c, f) : [];
  };
  /* Her picture on THIS section wins; her Me photo stands in for the banner
     and About only, because it is a photo of her. */
  const own = pictureOf(c);
  const base = { id: section.id, kind: kind.id, heading: str(c.head) || str(c.heading), sub: str(c.sub), button: str(c.btn) };
  switch (kind.id) {
    case "banner":
      return { ...base, eyebrow: str(c.eyebrow), headline: splitHeadline(c.head), picture: slot(own || identity.ownerPhotoUrl, kind.picture) };
    case "about":
      return { ...base, paras: listOf("paras").map(str).filter(Boolean), picture: slot(own || identity.ownerPhotoUrl, kind.picture) };
    case "services":
      return { ...base, items: listOf("items").map((r) => ({ name: str(r?.name), line: str(r?.line), price: str(r?.price) })).filter((r) => r.name || r.line || r.price) };
    case "testimonials":
      return {
        ...base,
        quotes: listOf("quotes").map((r) => ({ text: str(r?.text), who: str(r?.who), picture: slot(r?.photo, "Their photo") })).filter((r) => r.text || r.who),
      };
    case "faq":
      return { ...base, qs: listOf("qs").map((r) => ({ q: str(r?.q), a: str(r?.a) })).filter((r) => r.q || r.a) };
    case "freebie":
      return { ...base, picture: slot(own, kind.picture) };
    case "contact":
      return { ...base };
    case "cta":
      return { ...base };
    default:
      /* Other has no set place for a picture, so it is drawn only when she
         has added one — an empty spot here would be the preview asking. */
      return { ...base, kind: "other", text: str(c.text), picture: own ? slot(own, kind.picture) : null };
  }
}

/**
 * @param page      the page record — title, style
 * @param sections  its sections, in order
 * @param skin      brandSkin(...).skin
 * @param identity  { businessName, ownerName, logoUrl, ownerPhotoUrl, brand }
 * @param show      a style to SHOW instead of the page's, to compare — view only
 * @returns { blocks, look, logo, logoDark, name, drawnAs, notes }
 */
export function pageLayout({ page, sections = [], skin = {}, identity = {}, show = null } = {}) {
  const notes = [];
  const picked = styleDef(page?.style);
  const shown = styleDef(show);
  const drawnAs = (shown && DRAWN_STYLES.includes(shown.id) ? shown : null)
    || (picked && DRAWN_STYLES.includes(picked.id) ? picked : null)
    || styleDef("light_feminine");
  if (!picked) notes.push(`No style picked for this page${drawnAs.id === "light_feminine" ? ", so it is drawn as Light & Feminine" : ""}. Pick one in the page's settings.`);
  else if (picked.id !== drawnAs.id) notes.push(`Showing ${drawnAs.label} to compare. This page is set to ${picked.label}, and its word room is ${picked.label}'s.`);

  const written = (sections || []).filter((s) => sectionValues(s).length);
  const skipped = (sections || []).length - written.length;
  if (skipped > 0) notes.push(`${skipped} empty section${skipped === 1 ? " is" : "s are"} left out, as they are for the builder.`);

  const look = pageLook(skin, drawnAs.id);
  notes.push(...look.notes);
  if (!isLink(identity.ownerPhotoUrl) && written.some((s) => ["banner", "about"].includes(kindDef(s.sectionType).id) && !isLink(pictureOf(s.content)))) {
    notes.push("No photo of you yet, so a placeholder shows where it goes. Add one on the section, or in Me.");
  }

  return {
    blocks: written.map((s) => describe(s, identity)),
    look,
    logo: isLink(identity.logoUrl) ? str(identity.logoUrl) : "",
    /* Her logo for dark backgrounds (Me → Brand), for a header that sits on
       dark. Empty → the light one is used there. */
    logoDark: isLink(identity.brand?.logos?.dark) ? str(identity.brand.logos.dark) : "",
    name: str(identity.businessName) || str(identity.ownerName),
    drawnAs: drawnAs.id,
    notes,
  };
}
